/* eslint-disable complexity */
/* eslint-disable no-magic-numbers */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import classNames from 'classnames';
import { useFlags } from 'launchdarkly-react-client-sdk';

import Tooltip from 'carbon-react/lib/components/tooltip';
import PopoverContainer, { RenderOpenProps } from 'carbon-react/lib/components/popover-container';
import Button from 'carbon-react/lib/components/button';

import { FormattedMessage, useIntl } from '@futrli/localisation';
import { TooltipContainer } from '@futrli/ui-components';
import { CrudActivityContext, ROUTES, matchOnboardingRoute } from '@futrli/shared-services';
import { Forecast } from '@futrli-sdk/predict';
import { hasConsolidationSheetsImportEnabled } from '@futrli/organisations';

import {
  BUDGET_ONBOARDING_STEP,
  SCENARIO_ONBOARDING_STEP,
  SCENARIO_WITH_INVOICES_ONBOARDING_STEP
} from '../../../../forecast/onboarding/const';
import { trackEvent } from '../../../../../../predict/src/core/tracker';
import { TRACKING_EVENTS } from '../../../../../../predict/src/core/constants';
import arrow from './new-assumption-arrow.png';
import { ForecastType } from '../../../onboarding/shared/onboarding-type-select';
import { useAnimationLatency, useOnboardingUrlInfo } from '../../../../forecast/onboarding/utils/hooks';
import { usePredictionsInfoContext } from '../../context/predictions-info-context';
import { ForecastExcelUpload } from './forecast-excel-upload';

import * as styles from './new-assumption-button.scss';
import { PredictionBuilderContainer } from '../../../../forecast/prediction-builder/containers/prediction-builder-container';
import { usePredictManagementContext } from '../../../../predict-management/context/predict-management-context';

const shouldShowOnboardingArrow = (
  isOnboardingBudgetRoute: boolean,
  isOnboardingScenarioRoute: boolean,
  isOnboardingScenarioWithInvoicesRoute: boolean,
  onboardingStep: string,
  forecastType: string | null
): boolean => {
  return (
    (isOnboardingBudgetRoute &&
      onboardingStep === BUDGET_ONBOARDING_STEP.SCRATCH_CUSTOMISE &&
      forecastType === ForecastType.FromScratch) ||
    (isOnboardingBudgetRoute &&
      onboardingStep === BUDGET_ONBOARDING_STEP.LYA_CUSTOMISE &&
      forecastType === ForecastType.FromLYA) ||
    (isOnboardingBudgetRoute &&
      onboardingStep === BUDGET_ONBOARDING_STEP.AUTO_PREDICTIONS_CUSTOMISE &&
      forecastType === ForecastType.WithAutoPredictions) ||
    (isOnboardingScenarioRoute &&
      forecastType === ForecastType.FromScratch &&
      onboardingStep === SCENARIO_ONBOARDING_STEP.SCRATCH_CUSTOMISE) ||
    (isOnboardingScenarioRoute &&
      forecastType === ForecastType.WithAutoPredictions &&
      onboardingStep === SCENARIO_ONBOARDING_STEP.AUTO_PREDICTIONS_CUSTOMISE) ||
    (isOnboardingScenarioRoute &&
      forecastType === ForecastType.FromLYA &&
      onboardingStep === SCENARIO_ONBOARDING_STEP.LYA_CUSTOMISE) ||
    (isOnboardingScenarioWithInvoicesRoute &&
      forecastType === ForecastType.FromScratch &&
      onboardingStep === SCENARIO_WITH_INVOICES_ONBOARDING_STEP.SCRATCH_CUSTOMISE) ||
    (isOnboardingScenarioWithInvoicesRoute &&
      forecastType === ForecastType.WithAutoPredictions &&
      onboardingStep === SCENARIO_WITH_INVOICES_ONBOARDING_STEP.AUTO_PREDICTIONS_CUSTOMISE) ||
    (isOnboardingScenarioWithInvoicesRoute &&
      forecastType === ForecastType.FromLYA &&
      onboardingStep === SCENARIO_WITH_INVOICES_ONBOARDING_STEP.LYA_CUSTOMISE)
  );
};

export const NewAssumptionButton = ({ isOpenInitially }: { isOpenInitially?: boolean }) => {
  const flags = useFlags();
  const intl = useIntl();
  const navigate = useNavigate();
  const [isOpenExcelUpdate, setIsOpenExcelUpdate] = useState(false);
  const [isOpen, setIsOpen] = useState(isOpenInitially || false);
  const { workspaceId: tempWorkspaceId, organisationId: tempOrganisationId } = useParams();
  const workspaceId = tempWorkspaceId || '';
  const organisationId = tempOrganisationId || '';
  const { forecast, isConsolidation } = usePredictionsInfoContext();
  const { googleSheetImportAllowed } = usePredictManagementContext();
  const { pathname } = useLocation();
  const isOnboardingRoute = !!matchOnboardingRoute(pathname);
  const [isOpenManualPredictionDrawer, setIsOpenManualPredictionDrawer] = useState(false);

  const {
    onboardingStep,
    forecastType,
    isOnboardingBudgetRoute,
    isOnboardingScenarioRoute,
    isOnboardingScenarioWithInvoicesRoute
  } = useOnboardingUrlInfo();

  useEffect(() => {
    if (isOpenInitially) {
      setIsOpen(true);
    }
  }, [isOpenInitially]);

  const handleOpenMenu = () => {
    setIsOpen(true);
  };

  const handleCloseMenu = () => {
    setIsOpen(false);
  };

  const handleOpenExcelUpdate = useCallback(() => {
    setIsOpenExcelUpdate(true);
  }, []);

  const handleCloseExcelUpdate = useCallback(() => {
    setIsOpenExcelUpdate(false);
  }, []);

  const handleManualPredictionClick = () => setIsOpenManualPredictionDrawer(true);

  const handleCloseManualPredictionDrawer = useCallback(() => {
    setIsOpenManualPredictionDrawer(false);
  }, []);

  const handleImportTemplatesClick = () => {
    if (isOnboardingRoute) {
      return;
    }

    if (isConsolidation && !hasConsolidationSheetsImportEnabled(flags)) {
      return;
    }

    navigate(
      `${ROUTES.WORKSPACES_ROUTE}/${workspaceId}${
        isConsolidation ? ROUTES.CONSOLIDATIONS_ROUTE : ROUTES.ORGANISATIONS_ROUTE
      }/${organisationId}${ROUTES.PREDICT}${ROUTES.FORECASTS}/${forecast?.id}${ROUTES.IMPORT}`
    );

    trackEvent(TRACKING_EVENTS.USED_GOOGLE_SHEETS_IMPORT);
  };

  const showOnboardingArrow = shouldShowOnboardingArrow(
    isOnboardingBudgetRoute,
    isOnboardingScenarioRoute,
    isOnboardingScenarioWithInvoicesRoute,
    onboardingStep,
    forecastType
  );

  const showAnimatedArrow = useAnimationLatency(showOnboardingArrow);

  return (
    <>
      {showOnboardingArrow && (
        <img
          alt="onboarding-arrow"
          src={arrow}
          style={{ position: 'absolute', right: 260, top: 50 }}
          className={classNames(styles.fadeInContainer, {
            [styles.fadeIn]: showAnimatedArrow
          })}
        />
      )}
      <PopoverContainer
        p={0}
        open={isOpen}
        onClose={handleCloseMenu}
        onOpen={handleOpenMenu}
        ariaDescribedBy="new-assumption-popover"
        renderOpenComponent={({
          id,
          ref,
          onClick,
          'data-element': dataElement,
          'aria-label': ariaLabel,
          'aria-expanded': ariaExpanded,
          'aria-haspopup': ariaHasPopup
        }: RenderOpenProps) => (
          <TooltipContainer
            tooltipMessage={
              <FormattedMessage
                id="predict.predictions.budget_close_from_editing"
                defaultMessage="This budget is closed from further editing"
              />
            }
            position="bottom"
            data-testid="ds-icon-tooltip"
            disableTooltip={!(!isConsolidation && forecast?.kind === 'budget' && (forecast as Forecast)?.is_frozen)}
          >
            <Button
              iconType="dropdown"
              iconPosition="after"
              size="small"
              onClick={onClick}
              data-element={dataElement}
              aria-label={ariaLabel}
              aria-haspopup={ariaHasPopup}
              aria-expanded={ariaExpanded}
              ref={ref}
              id={id}
              disabled={!isConsolidation && forecast?.kind === 'budget' && (forecast as Forecast)?.is_frozen}
              ml={1}
            >
              {intl.formatMessage({ id: 'predict.predictions.new_prediction', defaultMessage: 'New prediction' })}
            </Button>
          </TooltipContainer>
        )}
        renderCloseComponent={() => <></>}
      >
        <div className={styles.option} onClick={handleManualPredictionClick}>
          <FormattedMessage id="predict.predictions.manual_prediction" defaultMessage="Manual prediction" />
        </div>
        <div
          className={classNames(styles.option, {
            [styles.disabledOption]:
              isOnboardingRoute ||
              (isConsolidation && !hasConsolidationSheetsImportEnabled(flags)) ||
              !googleSheetImportAllowed
          })}
          onClick={handleImportTemplatesClick}
        >
          {isOnboardingRoute ? (
            <Tooltip
              position="bottom"
              data-testid="ds-icon-tooltip"
              message={
                <FormattedMessage
                  id="predict.import_templates_will_be_enabled_after_onboarding"
                  defaultMessage="Import templates will be enabled after onboarding"
                />
              }
            >
              <div>
                <FormattedMessage id="predict.import_templates" defaultMessage="Import templates" />
              </div>
            </Tooltip>
          ) : (
            <FormattedMessage id="predict.import_templates" defaultMessage="Import templates" />
          )}
        </div>
        {!isConsolidation && !(forecast as Forecast)?.include_unpaid_invoices && (
          <div className={styles.option} onClick={handleOpenExcelUpdate}>
            <FormattedMessage id="common.excel_upload" defaultMessage="Excel upload" />
          </div>
        )}
      </PopoverContainer>
      <ForecastExcelUpload isOpenExcelUpdate={isOpenExcelUpdate} handleCloseExcelUpdate={handleCloseExcelUpdate} />
      {isOpenManualPredictionDrawer && (
        <CrudActivityContext>
          <PredictionBuilderContainer onClose={handleCloseManualPredictionDrawer} />
        </CrudActivityContext>
      )}
    </>
  );
};
